import { CurrentMoment } from '../CurrentMoment';
import type { ColorPalette } from '../ColorPalette';
import { AbstractMode } from './AbstractMode';
import type { ModeInterface } from './ModeInterface';
import type { ScheduleEventInterface } from './schedule-events/ScheduleEventInterface';

const MAX_TIMEOUT_MS = 2147483647;

/**
 * Schedule-driven palette mode (appearance preset: schedule, timezone basis).
 */
export class ScheduleColorMode extends AbstractMode implements ModeInterface {
	private timeoutId: ReturnType<typeof setTimeout> | null = null;

	constructor(private readonly scheduleEvents: ScheduleEventInterface[]) {
		super();
		this.scheduleNextBoundary();
	}

	currentPalette(): ColorPalette {
		let latestActiveEvent: ScheduleEventInterface | null = null;

		for (const scheduleEvent of this.scheduleEvents) {
			if (
				latestActiveEvent === null ||
				scheduleEvent.isCloserToNowThan(latestActiveEvent)
			) {
				latestActiveEvent = scheduleEvent;
			}
		}

		return latestActiveEvent!.getColorPalette();
	}

	nextEvent(): ScheduleEventInterface | null {
		let nextActiveEvent: ScheduleEventInterface | null = null;

		for (const scheduleEvent of this.scheduleEvents) {
			if (
				nextActiveEvent === null ||
				scheduleEvent.isCloserToNowInFutureThan(nextActiveEvent)
			) {
				nextActiveEvent = scheduleEvent;
			}
		}

		return nextActiveEvent;
	}

	type(): string {
		return 'schedule';
	}

	protected onDispose(): void {
		if (this.timeoutId !== null) {
			clearTimeout(this.timeoutId);
			this.timeoutId = null;
		}
	}

	private scheduleNextBoundary(): void {
		const nextEvent = this.nextEvent();
		if (!nextEvent) {
			return;
		}

		const nextAt = nextEvent.getNextBoundaryAfter();
		if (nextAt === null || Number.isNaN(nextAt)) {
			return;
		}

		let delay = nextAt - CurrentMoment.nowMs();
		if (delay < 1) {
			delay = 1;
		}
		if (delay > MAX_TIMEOUT_MS) {
			return;
		}

		this.timeoutId = setTimeout(() => {
			this.timeoutId = null;
			this.notifyPaletteStale();
			this.scheduleNextBoundary();
		}, delay);
	}
}
